import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton, OrDivider } from "@/components/GoogleButton";
import { setTheme } from "@/lib/theme";
import { Loader2, Check, ArrowRight } from "lucide-react";

const PROOF = [
  "Brand DNA extracted from any website in 60 seconds",
  "On-brand posts for Facebook, Instagram & TikTok",
  "Swipe to approve — we publish on autopilot",
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({
    appName: "HDG Creative Studio",
    poweredByUrl: "https://heydesignguy.com",
    contactEmail: "hello@heydesignguy.com",
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTheme("workspace");
    api.get("/public-config").then(({ data }) => setCfg((c) => ({ ...c, ...data }))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username, password);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  const contactHref = cfg.contactEmail ? `mailto:${cfg.contactEmail}` : cfg.poweredByUrl;

  return (
    <div data-testid="login-page" className="lightboard grid grid-cols-1 lg:grid-cols-2" style={{ minHeight: "100vh", background: "var(--surface)" }}>
        {/* LEFT — dark premium brand panel */}
        <aside
          data-testid="login-brand-panel"
          className="hidden lg:flex"
          style={{
            position: "relative", flexDirection: "column", justifyContent: "space-between",
            padding: "56px 56px 48px", background: "#0E0E11", overflow: "hidden",
          }}
        >
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
          <div style={{ position: "absolute", top: "-20%", right: "-10%", width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,107,44,0.18), transparent 62%)", pointerEvents: "none" }} />

          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/hdg-logo.jpg" alt="HeyDesignGuy" style={{ height: 38, width: 38, borderRadius: 10, objectFit: "contain", background: "#fff" }} />
            <span className="font-display" style={{ color: "#fff", fontWeight: 700, fontSize: 17, letterSpacing: "-0.01em" }}>HeyDesignGuy</span>
          </div>

          <div style={{ position: "relative", maxWidth: 520 }}>
            <h2 className="font-display" data-testid="brand-headline" style={{ color: "#fff", fontWeight: 700, fontSize: 40, lineHeight: 1.08, letterSpacing: "-0.025em", margin: "0 0 18px" }}>
              Your brand, AI-powered creative — <span style={{ color: "#FF6B2C" }}>one click.</span>
            </h2>
            <p className="font-body" style={{ color: "rgba(255,255,255,0.72)", fontSize: 16, lineHeight: 1.6, margin: "0 0 34px", maxWidth: 480 }}>
              Paste a URL. Watch your brand become a content engine — research, ideas, on-brand visuals, and scheduled posts in about a minute.
            </p>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 16 }}>
              {PROOF.map((p, i) => (
                <li key={i} data-testid={`proof-point-${i}`} style={{ display: "flex", alignItems: "center", gap: 13 }}>
                  <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 999, display: "grid", placeItems: "center", background: "rgba(255,107,44,0.16)", border: "1px solid rgba(255,107,44,0.45)" }}>
                    <Check size={13} style={{ color: "#FF6B2C" }} strokeWidth={3} />
                  </span>
                  <span className="font-body" style={{ color: "rgba(255,255,255,0.9)", fontSize: 14.5 }}>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div style={{ position: "relative" }} />
        </aside>

        {/* RIGHT — clean light form */}
        <main style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px", background: "var(--surface)", position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "radial-gradient(circle, #E4E4E7 1px, transparent 1px)", backgroundSize: "26px 26px", opacity: 0.4 }} />
          <form onSubmit={submit} className="panel-card" data-testid="login-card" style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 408, padding: 38, background: "#fff" }}>
            <button type="button" onClick={() => navigate("/")} data-testid="login-home" aria-label="HeyDesignGuy — home" style={{ display: "flex", alignItems: "center", gap: 11, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 30 }}>
              <img src="/hdg-logo.jpg" alt="HeyDesignGuy" data-testid="login-logo" style={{ height: 38, width: 38, objectFit: "contain", borderRadius: 10, background: "#fff", border: "1px solid var(--cardborder)" }} />
              <span className="font-display" style={{ fontWeight: 700, fontSize: 15.5, color: "var(--tprimary)", letterSpacing: "-0.01em" }}>HeyDesignGuy</span>
            </button>

            <h1 className="font-display" style={{ fontSize: 27, fontWeight: 700, letterSpacing: "-0.02em", margin: "0 0 7px", color: "var(--tprimary)" }}>
              Welcome back
            </h1>
            <p className="font-body" style={{ fontSize: 14, color: "var(--tsecondary)", margin: "0 0 26px", lineHeight: 1.5 }}>
              Sign in to your Creative Studio workspace.
            </p>

            <GoogleButton label="Sign in with Google" testId="login-google-btn" />
            <OrDivider />

            <label className="font-mono" style={{ fontSize: 11, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Email</label>
            <input
              data-testid="login-username"
              className="dim-input"
              style={{ marginTop: 6, marginBottom: 18 }}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="you@heydesignguy.com"
              autoComplete="username"
            />

            <label className="font-mono" style={{ fontSize: 11, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Password</label>
            <input
              data-testid="login-password"
              type="password"
              className="dim-input"
              style={{ marginTop: 6, marginBottom: 8 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />

            {error && (
              <div data-testid="login-error" className="font-mono" style={{ color: "var(--err)", fontSize: 12, margin: "10px 0" }}>
                {error}
              </div>
            )}

            <button data-testid="login-submit" type="submit" className="btn-primary" disabled={busy} style={{ width: "100%", marginTop: 16, padding: "12px" }}>
              {busy ? <Loader2 size={16} className="spin" /> : null}
              {busy ? "Signing in..." : "Sign in"}
            </button>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              <button type="button" data-testid="login-create-workspace" onClick={() => navigate("/start")} className="app-link font-body" style={{ fontSize: 13, background: "none", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 5, color: "var(--tprimary)", fontWeight: 600 }}>
                Create a workspace <ArrowRight size={13} />
              </button>
              <span className="font-body" style={{ fontSize: 12, color: "var(--tmuted)", display: "block", marginTop: 2 }}>for new clients</span>
            </div>

            <div style={{ borderTop: "1px solid var(--cardborder)", marginTop: 26, paddingTop: 18, textAlign: "center" }}>
              <p className="font-body" data-testid="login-microcopy" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "0 0 10px", fontStyle: "italic" }}>
                Agency-caliber creative, without the agency retainer.
              </p>
              <div className="font-body" style={{ fontSize: 12, color: "var(--tmuted)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <button type="button" data-testid="login-pricing" onClick={() => navigate("/pricing")} className="app-link" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, font: "inherit" }}>Pricing</button>
                <span>·</span>
                <a data-testid="login-forgot" href={contactHref} className="app-link">Forgot password</a>
                <span>·</span>
                <a data-testid="login-contact" href={cfg.poweredByUrl} target="_blank" rel="noreferrer" className="app-link">Contact HeyDesignGuy</a>
              </div>
            </div>
          </form>
        </main>
    </div>
  );
}
